import {
  FormFieldInjectedProps,
  withLabelAndValidation
} from '@unistocks/components/form/components/with-label-and-validation';
import { FormComponentPropsBase, FormFieldOmittedProps } from '@unistocks/components/form/model';
import { Select, SelectProps, SelectRef } from '@unistocks/components/select/select';
import { t } from '@unistocks/localization';
import { isFunction } from '@unistocks/shared/utils/util';
import { memo, useCallback, useImperativeHandle, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useComposedRefs } from 'tamagui';

export type FormSelectProps<
  TItem = any,
  TFieldValues extends FieldValues = FieldValues
> = FormComponentPropsBase<TFieldValues> &
  Omit<
    SelectProps<TItem>,
    FormFieldOmittedProps | 'value' | 'open' | 'onOpenChange' | 'onChange'
  > & {
    onChange?: SelectProps<TItem>['onChange'];
  };

function FormSelectComponent<
  TItem = any,
  TFieldValues extends FieldValues = FieldValues
>({
  ref,
  name,
  id,
  field,
  fieldState,
  isRequired,
  isDisabled,
  valueTransform,
  dependsOn,
  ...props
}: FormSelectProps<TItem, TFieldValues> & FormFieldInjectedProps) {
  const composedRef = useComposedRefs(ref, field.ref);

  const [isOpen, setIsOpen] = useState(false);

  // Expose focus and blur methods to react hook form
  useImperativeHandle(composedRef, () => ({
    focus: () => setIsOpen(true),
    blur: () => setIsOpen(false)
  }));

  const onOpenChange = useCallback(
    (nextValue: boolean) => {
      setIsOpen(nextValue);

      if (!nextValue) {
        field.onBlur();
      }
    },
    [field, setIsOpen]
  );

  const onValueChange = useCallback(
    (newValue: TItem[] | TItem | null) => {
      let transformedValue = newValue;
      if (isFunction(valueTransform?.output)) {
        transformedValue = valueTransform.output(newValue);
      }

      field.onChange(transformedValue);
      props.onChange?.(newValue as any); // Type assertion needed due to union type constraints
    },
    [field, props, valueTransform]
  );

  return (
    <Select
      headerTitle={
        props.label ? t('::SelectWhat', { 0: props.label.toLocaleLowerCase() }) : undefined
      }
      {...props}
      ref={composedRef}
      id={id}
      value={valueTransform?.input?.(field.value) ?? field.value}
      isRequired={isRequired}
      isDisabled={isDisabled}
      invalid={fieldState.invalid}
      open={isOpen}
      onOpenChange={onOpenChange}
      onChange={onValueChange}
    />
  );
}

export const FormSelect = withLabelAndValidation<SelectRef, FormSelectProps>(
  memo(FormSelectComponent)
) as <TItem = any, TFieldValues extends FieldValues = FieldValues>(
  props: FormSelectProps<TItem, TFieldValues>
) => React.ReactElement<FormSelectProps<TItem, TFieldValues>>;
